import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { AnyDate, parseDate } from '../../helpers/dateHelper'
import FormattedDateRange, { FormattedDateRangeProps } from './FormattedDateRange'
import { Button, makeStyles, Popover, PopoverSurface, PopoverTrigger, PositioningImperativeRef, Subtitle2, tokens } from '@fluentui/react-components'
import { useToggle } from 'usehooks-ts'
import { Calendar } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import { defaultDatePickerProps } from '../DatePicker/LocalDatePicker'

export const useStyles = makeStyles({
    popoverSurface: {
        // width: 'fit-content',
        // height: 'fit-content',
        display: 'grid',
        gridTemplateRows: '40px auto 32px',
        rowGap: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground3,
    },
    popoverContent: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    daterange: {
        padding: tokens.spacingHorizontalXS,

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2,
            borderRadius: tokens.borderRadiusMedium,
        },
    },
})

type EditableFormattedDateRangeProps = FormattedDateRangeProps & {
    setDate: (date: AnyDate) => void
}

export default function EditableFormattedDateRange(props: EditableFormattedDateRangeProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const dateValue: Date = useMemo(() => new Date(parseDate(props.date || new Date()).toBSON()), [props.date])

    const [isOpen, toggleOpen, setOpen] = useToggle(false)
    const positioningRef = React.useRef<PositioningImperativeRef>(null)

    const triggerRef = React.useCallback(
        (el: HTMLElement) => {
            positioningRef.current?.setTarget(el)
        },
        [positioningRef],
    )

    return (
        <Popover {...props} open={isOpen} onOpenChange={(_event, data) => setOpen(data.open)} positioning={{ positioningRef }}>
            <PopoverTrigger disableButtonEnhancement>
                <div ref={triggerRef} className={styles.daterange}>
                    <FormattedDateRange {...props} />
                </div>
            </PopoverTrigger>

            <PopoverSurface tabIndex={-1} className={styles.popoverSurface}>
                <Subtitle2>{translate('base.datepicker.placeholder')}</Subtitle2>
                <div className={styles.popoverContent}>
                    <Calendar
                        {...defaultDatePickerProps}
                        showMonthPickerAsOverlay
                        highlightSelectedMonth
                        value={dateValue}
                        onSelectDate={date => {
                            props.setDate(date)
                            setOpen(false)
                        }}
                    />
                </div>
                <Button appearance={'secondary'} onClick={() => toggleOpen()}>
                    {translate('base.alert.cancel')}
                </Button>
            </PopoverSurface>
        </Popover>
    )
}
